import React, { useState, useEffect } from 'react';
import { Mail, Check, Eye, Trash2, Send, X } from 'lucide-react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showReplyDialog, setShowReplyDialog] = useState(false);
  const [replyData, setReplyData] = useState({ subject: '', message: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => { fetchMessages(); }, []);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/contact`, { withCredentials: true });
      setMessages(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/contact/${id}/read`, {}, { withCredentials: true });
      fetchMessages();
    } catch (e) { toast.error('Failed to update'); }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await axios.delete(`${API}/contact/${id}`, { withCredentials: true });
      toast.success('Message deleted');
      if (selectedMessage?.id === id) setSelectedMessage(null);
      fetchMessages();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const openReplyDialog = () => {
    setReplyData({
      subject: `Re: Message from ${selectedMessage.name}`,
      message: ''
    });
    setShowReplyDialog(true);
  };

  const sendReply = async () => {
    if (!replyData.subject.trim() || !replyData.message.trim()) {
      toast.error('Please fill in subject and message');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/admin/send-email`, {
        to_email: selectedMessage.email,
        to_name: selectedMessage.name,
        subject: replyData.subject,
        message: replyData.message
      }, { withCredentials: true });
      toast.success('Email sent successfully!');
      setShowReplyDialog(false);
      setReplyData({ subject: '', message: '' });
    } catch (e) {
      toast.error('Failed to send email. Check email configuration.');
    } finally {
      setSending(false);
    }
  };

  const unreadCount = messages.filter(m => !m.is_read).length;

  return (
    <div data-testid="admin-messages">
      <div className="mb-8">
        <h1 className="font-serif text-3xl text-charcoal">Messages</h1>
        <p className="text-charcoal/60 mt-1">
          {unreadCount > 0 ? `${unreadCount} unread message${unreadCount > 1 ? 's' : ''}` : 'Contact form submissions'}
        </p>
      </div>

      {loading ? (
        <div className="text-center py-12"><div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto" /></div>
      ) : messages.length > 0 ? (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Message List */}
          <div className="lg:col-span-1 space-y-2 max-h-[600px] overflow-y-auto">
            {messages.map((msg) => (
              <div
                key={msg.id}
                onClick={() => { setSelectedMessage(msg); if (!msg.is_read) markAsRead(msg.id); }}
                className={`p-4 rounded-xl cursor-pointer transition-colors ${
                  selectedMessage?.id === msg.id ? 'bg-burgundy/10 border border-burgundy/20' :
                  msg.is_read ? 'bg-white border border-gold/10' : 'bg-gold/10 border border-gold/30'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className={`font-medium ${msg.is_read ? 'text-charcoal' : 'text-charcoal'}`}>{msg.name}</p>
                    <p className="text-sm text-charcoal/60 truncate">{msg.email}</p>
                  </div>
                  {!msg.is_read && <span className="w-2 h-2 bg-gold rounded-full" />}
                </div>
                <p className="text-sm text-charcoal/50 mt-2 line-clamp-2">{msg.message}</p>
                <p className="text-xs text-charcoal/40 mt-2">{new Date(msg.created_at).toLocaleDateString()}</p>
              </div>
            ))}
          </div>

          {/* Message Detail */}
          <div className="lg:col-span-2">
            {selectedMessage ? (
              <div className="bg-white rounded-2xl p-6 border border-gold/10">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="font-serif text-xl text-charcoal">{selectedMessage.name}</h2>
                    <p className="text-charcoal/60">{selectedMessage.email}</p>
                    {selectedMessage.phone && <p className="text-charcoal/60">{selectedMessage.phone}</p>}
                  </div>
                  <span className="text-sm text-charcoal/40">
                    {new Date(selectedMessage.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="bg-cream rounded-xl p-4">
                  <p className="text-charcoal whitespace-pre-wrap">{selectedMessage.message}</p>
                </div>
                <div className="mt-4 flex gap-2">
                  <button 
                    onClick={openReplyDialog}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-burgundy text-white rounded-lg text-sm hover:bg-purple transition-colors"
                  >
                    <Send size={16} /> Reply via Email
                  </button>
                  <button 
                    onClick={() => deleteMessage(selectedMessage.id)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600 transition-colors"
                  >
                    <Trash2 size={16} /> Delete
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl p-12 border border-gold/10 text-center">
                <Mail size={48} className="mx-auto text-charcoal/20 mb-4" />
                <p className="text-charcoal/50">Select a message to view</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-2xl border border-gold/10">
          <Mail size={48} className="mx-auto text-charcoal/20 mb-4" />
          <p className="text-charcoal/50">No messages yet</p>
        </div>
      )}

      {/* Reply Dialog */}
      <Dialog open={showReplyDialog} onOpenChange={setShowReplyDialog}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Reply to {selectedMessage?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>To</Label>
              <Input value={selectedMessage?.email || ''} disabled className="bg-cream/50" />
            </div>
            <div>
              <Label>Subject</Label>
              <Input
                value={replyData.subject}
                onChange={(e) => setReplyData({...replyData, subject: e.target.value})}
                placeholder="Email subject"
              />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                value={replyData.message}
                onChange={(e) => setReplyData({...replyData, message: e.target.value})}
                placeholder="Write your reply..."
                rows={6}
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={sendReply}
                disabled={sending}
                className="flex-1 bg-burgundy hover:bg-purple"
              >
                <Send size={16} className="mr-2" /> {sending ? 'Sending...' : 'Send Email'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowReplyDialog(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
